import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Minus, ChevronDown, X, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/format";

const COLOURS = [
  { key: "noir", label: "Noir", slug: "signature-mini-noir", swatch: "#141210", accent: "#141210" },
  { key: "ivory", label: "Ivory", slug: "signature-mini-ivory", swatch: "#EFEAE0", accent: "#C7BFA9" },
  { key: "sable", label: "Sable", slug: "signature-mini-sable", swatch: "#B49A7A", accent: "#8B7454" },
];

const PLACEHOLDER = "#D8D4CB";

function galleryFor(product) {
  const imgs = product?.images ?? [];
  if (imgs.length === 0) return [];
  // If we have multiple real product photos, use them as-is (no cropping).
  if (imgs.length > 1) {
    return imgs.map((src) => ({ src, pos: "center center" }));
  }
  // Fallback (single-image variants): synthesise a 4-tile placeholder gallery
  // by shifting the object-position so the thumbnails feel varied.
  const base = imgs[0];
  return [
    { src: base, pos: "center 40%" },
    { src: base, pos: "center 60%" },
    { src: base, pos: "center 25%" },
    { src: base, pos: "center 80%" },
  ];
}

function Placeholder({ ratio = "aspect-[4/5]", className = "", testid }) {
  return (
    <div
      className={`${ratio} ${className}`}
      style={{ background: PLACEHOLDER }}
      data-testid={testid}
    />
  );
}

function Accordion({ title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t" style={{ borderColor: "rgba(26,26,25,0.12)" }} data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full py-5 flex justify-between items-center text-left"
        aria-expanded={open}
      >
        <span
          className="font-display tracking-[0.22em] uppercase"
          style={{ fontSize: "12px", fontWeight: 500, color: "#0E0E0D" }}
        >
          {title}
        </span>
        <ChevronDown
          size={16}
          strokeWidth={1.5}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 300ms ease",
          }}
        />
      </button>
      <div
        style={{
          maxHeight: open ? 600 : 0,
          overflow: "hidden",
          transition: "max-height 400ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <div className="pb-6 pr-6 text-sm leading-[1.85]" style={{ color: "rgba(26,26,25,0.78)" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function EditorialChapter({ colour, editorialTitle, description, layout, cta, onCTAClick }) {
  return (
    <section
      className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-28"
      data-testid={`chapter-${colour.key}`}
    >
      <header className="max-w-3xl mx-auto text-center">
        <h2
          className="font-display tracking-[0.32em] uppercase"
          style={{
            fontSize: "clamp(1rem, 1.4vw, 1.25rem)",
            fontWeight: 500,
            color: "#0E0E0D",
          }}
        >
          {colour.label}
        </h2>
        <div
          className="mx-auto mt-5"
          style={{ width: 44, height: 1, background: colour.accent, opacity: 0.75 }}
        />
        <h3
          className="font-display mt-8 leading-[1.1]"
          style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)", fontWeight: 400, letterSpacing: "-0.005em" }}
        >
          {editorialTitle}
        </h3>
        <p
          className="mt-6 text-base sm:text-[17px] leading-[1.85] mx-auto"
          style={{ color: "rgba(26,26,25,0.78)", maxWidth: "580px" }}
        >
          {description}
        </p>
      </header>

      <div className="mt-16">{layout}</div>

      <div className="mt-14 text-center">
        <button
          onClick={onCTAClick}
          className="discover-link"
          style={{ color: "#1A1A19" }}
          data-testid={`chapter-cta-${colour.key}`}
        >
          <span>{cta}</span>
          <span className="discover-arrow">→</span>
        </button>
      </div>
    </section>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [mainIdx, setMainIdx] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);

  const heroRef = useRef(null);

  const colourKey = params.get("colour") || "noir";
  const colour = COLOURS.find((c) => c.key === colourKey) || COLOURS[0];

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, []);

  const product = useMemo(
    () => products.find((p) => p.slug === colour.slug),
    [products, colour.slug]
  );
  const gallery = useMemo(() => galleryFor(product), [product]);

  useEffect(() => {
    setMainIdx(0);
    if (product && !size) setSize(product.sizes?.[0] || null);
  }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lightbox: close on ESC + lock body scroll while open
  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [zoomOpen]);

  const selectColour = (key) => {
    setParams({ colour: key });
    setMainIdx(0);
  };

  const jumpToHero = (key) => {
    selectColour(key);
    if (heroRef.current) {
      heroRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleAdd = () => {
    if (!product) return;
    if (!size) {
      toast.error("Select a size first");
      return;
    }
    for (let i = 0; i < qty; i += 1) addItem(product, size, 1);
    toast.success(`Signature Mini — ${colour.label} added to your bag`);
  };

  return (
    <main
      className="min-h-screen"
      style={{ background: "#F7F5F2", color: "#1A1A19" }}
      data-testid="shop-page"
    >
      {/* ==================== PRODUCT HERO ==================== */}
      <section
        ref={heroRef}
        className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 sm:pt-20 pb-16 sm:pb-20"
        data-testid="product-hero"
      >
        {loading || !product ? (
          <div className="min-h-[60vh] flex items-center justify-center text-sm opacity-60">
            Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Gallery */}
            <div className="lg:col-span-7">
              <button
                type="button"
                onClick={() => setZoomOpen(true)}
                className="group relative block w-full aspect-[4/5] overflow-hidden cursor-zoom-in"
                style={{ background: "#EFEBE4" }}
                data-testid="sm-gallery-main"
                aria-label="Open zoom view"
              >
                <img
                  src={gallery[mainIdx]?.src}
                  alt={product.name}
                  loading="eager"
                  className="w-full h-full object-contain transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]"
                  style={{ objectPosition: gallery[mainIdx]?.pos }}
                />
                <span
                  className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: "rgba(247,245,242,0.92)",
                    color: "#1A1A19",
                    fontSize: "10px",
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    fontWeight: 500,
                    backdropFilter: "blur(6px)",
                  }}
                  aria-hidden
                >
                  <ZoomIn size={12} strokeWidth={1.5} /> Zoom
                </span>
              </button>
              <div className="grid grid-cols-5 gap-2 sm:gap-3 mt-4" data-testid="sm-gallery-thumbs">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setMainIdx(i)}
                    className="aspect-square overflow-hidden"
                    style={{
                      background: "#EFEBE4",
                      outline: mainIdx === i ? "1px solid #1A1A19" : "1px solid transparent",
                      outlineOffset: "2px",
                      transition: "outline-color 200ms ease",
                    }}
                    aria-label={`Gallery ${i + 1}`}
                    data-testid={`sm-thumb-${i}`}
                  >
                    <img
                      src={img.src}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: "center 30%" }}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product info */}
            <div className="lg:col-span-5 lg:pl-4">
              <p
                className="text-[11px] tracking-[0.32em] uppercase"
                style={{ color: "rgba(26,26,25,0.55)" }}
              >
                Introducing
              </p>
              <h1
                className="font-display mt-5 leading-[1.05]"
                style={{ fontSize: "clamp(2.25rem, 3.5vw, 3rem)", fontWeight: 400, letterSpacing: "-0.005em" }}
                data-testid="sm-title"
              >
                The <span className="italic">Signature</span> Mini
              </h1>

              {/* Price */}
              <div className="flex items-baseline flex-wrap gap-3 mt-5" data-testid="sm-price">
                <span className="font-display" style={{ fontSize: "1.5rem", color: "#0E0E0D" }}>
                  {formatPrice(product.price, product.currency)}
                </span>
                {product.original_price && product.original_price > product.price && (
                  <>
                    <span className="text-sm" style={{ color: "rgba(26,26,25,0.5)" }}>
                      MRP <span className="line-through">{formatPrice(product.original_price, product.currency)}</span>
                    </span>
                    <span
                      className="text-[10px] tracking-[0.24em] uppercase px-3 py-1"
                      style={{ background: "#1A1A19", color: "#F7F5F2", fontWeight: 500 }}
                    >
                      Launch Offer · 20% Off
                    </span>
                  </>
                )}
              </div>

              {/* Colour */}
              <div className="mt-8" data-testid="sm-colour-selector">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] tracking-[0.28em] uppercase" style={{ color: "rgba(26,26,25,0.55)" }}>
                    Colour
                  </span>
                  <span className="text-[11px] tracking-[0.28em] uppercase font-display" style={{ color: "#0E0E0D", fontWeight: 500 }}>
                    {colour.label}
                  </span>
                </div>
                <div className="flex gap-4 mt-4">
                  {COLOURS.map((c) => {
                    const selected = c.key === colour.key;
                    return (
                      <button
                        key={c.key}
                        onClick={() => selectColour(c.key)}
                        aria-label={c.label}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: c.swatch,
                          outline: selected ? "1px solid #1A1A19" : "1px solid rgba(26,26,25,0.2)",
                          outlineOffset: selected ? "3px" : "2px",
                          transition: "outline-offset 200ms ease",
                          cursor: "pointer",
                        }}
                        data-testid={`sm-colour-${c.key}`}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Size */}
              <div className="mt-8" data-testid="sm-size-selector">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] tracking-[0.28em] uppercase" style={{ color: "rgba(26,26,25,0.55)" }}>
                    Size
                  </span>
                  <Link to="/sizing-fit" className="text-[11px] uppercase tracking-[0.22em] underline underline-offset-4 opacity-60 hover:opacity-100">
                    Size Guide
                  </Link>
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={"size-pill" + (size === s ? " selected" : "")}
                      data-testid={`sm-size-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mt-8" data-testid="sm-qty-selector">
                <span className="text-[11px] tracking-[0.28em] uppercase" style={{ color: "rgba(26,26,25,0.55)" }}>
                  Quantity
                </span>
                <div className="inline-flex items-center mt-4" style={{ border: "1px solid rgba(26,26,25,0.2)" }}>
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-2" aria-label="decrease" data-testid="sm-qty-dec">
                    <Minus size={14} strokeWidth={1.5} />
                  </button>
                  <span className="px-6 text-sm font-display" style={{ minWidth: 40, textAlign: "center" }} data-testid="sm-qty-value">
                    {qty}
                  </span>
                  <button onClick={() => setQty(Math.min(10, qty + 1))} className="px-4 py-2" aria-label="increase" data-testid="sm-qty-inc">
                    <Plus size={14} strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {/* Add to Cart */}
              <button onClick={handleAdd} className="btn-primary w-full mt-8" data-testid="sm-add-to-cart">
                Add to Bag — {formatPrice(product.price * qty, product.currency)}
              </button>

              {/* Trust highlights */}
              <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[11px] tracking-[0.16em] uppercase" style={{ color: "rgba(26,26,25,0.72)" }} data-testid="sm-trust">
                <li className="inline-flex items-center gap-2">
                  <span style={{ color: "#0E0E0D" }} aria-hidden>✓</span> Made in India
                </li>
                <li className="inline-flex items-center gap-2">
                  <span style={{ color: "#0E0E0D" }} aria-hidden>✓</span> Free Shipping
                </li>
                <li className="inline-flex items-center gap-2">
                  <span style={{ color: "#0E0E0D" }} aria-hidden>✓</span> Easy Size Exchange
                  <Link to="/exchanges-returns" className="underline underline-offset-2 opacity-70 hover:opacity-100 lowercase tracking-normal normal-case ml-1">
                    Read T&amp;Cs
                  </Link>
                </li>
              </ul>

              {/* Accordions */}
              <div className="mt-8" data-testid="sm-accordions">
                <Accordion title="Description" defaultOpen testid="sm-acc-description">
                  {product.description}
                </Accordion>
                <Accordion title="Shipping" testid="sm-acc-shipping">
                  <p>
                    Complimentary shipping across Delhi. Shipping charges for orders outside Delhi
                    are calculated at checkout. Orders are dispatched within 2–5 business days and
                    typically delivered within 7 business days.
                  </p>
                  <p className="mt-3">
                    <Link to="/shipping-delivery" className="underline underline-offset-4">Full shipping policy →</Link>
                  </p>
                </Accordion>
                <Accordion title="Care Instructions" testid="sm-acc-care">
                  <p>{product.care}</p>
                  <p className="mt-3">
                    <Link to="/care-instructions" className="underline underline-offset-4">Full care instructions →</Link>
                  </p>
                </Accordion>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ==================== COLLECTION INTRO ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-24 text-center"
        data-testid="collection-intro"
      >
        <p className="text-[11px] tracking-[0.32em] uppercase" style={{ color: "rgba(26,26,25,0.55)" }}>
          The Signature Mini
        </p>
        <h2
          className="font-display mt-8 leading-[1.05]"
          style={{ fontSize: "clamp(2.25rem, 4vw, 3.25rem)", fontWeight: 400, letterSpacing: "-0.005em" }}
        >
          One silhouette. <span className="italic">Three signatures.</span>
        </h2>
        <p
          className="mt-8 mx-auto text-base sm:text-[17px] leading-[1.85]"
          style={{ color: "rgba(26,26,25,0.78)", maxWidth: "560px" }}
        >
          A timeless silhouette, interpreted in three signature shades.
        </p>
      </section>

      {/* ==================== SECTION 1 — NOIR ==================== */}
      <EditorialChapter
        colour={COLOURS[0]}
        editorialTitle={
          <>
            Quiet <span className="italic">Confidence.</span>
          </>
        }
        description="A timeless black mini designed to transition effortlessly from day to evening."
        cta="Discover Noir"
        onCTAClick={() => jumpToHero("noir")}
        layout={
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="md:col-span-2 md:row-span-2">
              <Placeholder ratio="aspect-[4/5]" testid="noir-front" />
            </div>
            <Placeholder ratio="aspect-[4/5]" testid="noir-side-zip" />
            <Placeholder ratio="aspect-[4/5]" testid="noir-back-dart" />
          </div>
        }
      />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div style={{ height: 1, background: "rgba(26,26,25,0.10)" }} />
      </div>

      {/* ==================== SECTION 2 — IVORY ==================== */}
      <EditorialChapter
        colour={COLOURS[1]}
        editorialTitle={
          <>
            Light. Effortless. <span className="italic">Timeless.</span>
          </>
        }
        description="Clean, understated and endlessly versatile."
        cta="Discover Ivory"
        onCTAClick={() => jumpToHero("ivory")}
        layout={
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <div className="flex flex-col gap-6 sm:gap-8">
              <Placeholder ratio="aspect-[4/5]" testid="ivory-walking" />
              <Placeholder ratio="aspect-[4/5]" testid="ivory-waist" />
            </div>
            <Placeholder ratio="aspect-[4/5] md:aspect-auto md:h-full" testid="ivory-movement" />
          </div>
        }
      />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div style={{ height: 1, background: "rgba(26,26,25,0.10)" }} />
      </div>

      {/* ==================== SECTION 3 — SABLE ==================== */}
      <EditorialChapter
        colour={COLOURS[2]}
        editorialTitle={
          <>
            Warm <span className="italic">Neutrality.</span>
          </>
        }
        description="An effortless neutral designed for every wardrobe."
        cta="Discover Sable"
        onCTAClick={() => jumpToHero("sable")}
        layout={
          <div className="flex flex-col gap-6 sm:gap-8">
            <Placeholder ratio="aspect-[16/9]" testid="sable-editorial" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              <Placeholder ratio="aspect-square" testid="sable-front" />
              <Placeholder ratio="aspect-square" testid="sable-back" />
              <Placeholder ratio="aspect-square" testid="sable-detail" />
            </div>
          </div>
        }
      />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div style={{ height: 1, background: "rgba(26,26,25,0.10)" }} />
      </div>

      {/* ==================== REPEAT WEAR ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-28 text-center"
        data-testid="repeat-wear"
      >
        <p className="text-[11px] tracking-[0.32em] uppercase" style={{ color: "rgba(26,26,25,0.55)" }}>
          Made to Live With
        </p>
        <h2
          className="font-display mt-8 leading-[1.05]"
          style={{ fontSize: "clamp(1.875rem, 3.2vw, 2.75rem)", fontWeight: 400 }}
        >
          Designed for <span className="italic">Repeat Wear</span>
        </h2>
        <p
          className="mt-8 mx-auto text-base sm:text-lg leading-[1.85]"
          style={{ color: "rgba(26,26,25,0.78)", maxWidth: "44ch" }}
        >
          Designed to move effortlessly between occasions. A timeless silhouette created to be
          worn often and kept for years.
        </p>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div style={{ height: 1, background: "rgba(26,26,25,0.10)" }} />
      </div>

      {/* ==================== SHIPPING & DELIVERY ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-24"
        data-testid="shipping-block"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-[11px] tracking-[0.32em] uppercase" style={{ color: "rgba(26,26,25,0.55)" }}>
              Client Care
            </p>
            <h2
              className="font-display mt-6 leading-[1.1]"
              style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.25rem)", fontWeight: 400 }}
            >
              Shipping <span className="italic">& Delivery</span>
            </h2>
          </div>
          <div className="md:col-span-8">
            <ul
              className="space-y-4 text-base leading-[1.85]"
              style={{ color: "rgba(26,26,25,0.78)" }}
              data-testid="shipping-list"
            >
              <li>· Free shipping on all prepaid orders across India.</li>
              <li>· Orders are typically processed within 1–2 business days.</li>
              <li>
                · Once dispatched, delivery usually takes:
                <ul className="mt-2 ml-6 space-y-1 text-[15px]" style={{ color: "rgba(26,26,25,0.7)" }}>
                  <li>— Delhi NCR: 1–3 business days</li>
                  <li>— Rest of India: 3–7 business days</li>
                </ul>
              </li>
              <li>
                · Delivery timelines may vary during public holidays, launch periods or unforeseen
                courier delays.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ==================== SUPPORT ==================== */}
      <section
        className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pb-24 sm:pb-32"
        data-testid="support-block"
      >
        <div className="text-center py-14 sm:py-16 px-8" style={{ background: "#EFEBE4" }}>
          <h2
            className="font-display"
            style={{ fontSize: "clamp(1.5rem, 2.25vw, 2rem)", fontWeight: 400, color: "#0E0E0D" }}
          >
            Need Assistance?
          </h2>
          <a
            href="mailto:support@tamorebytamanna.com"
            className="mt-6 font-display italic underline underline-offset-[6px] hover:opacity-70 transition-opacity inline-block"
            style={{ fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)", color: "#0E0E0D" }}
          >
            support@tamorebytamanna.com
          </a>
          <p className="mt-4 text-sm" style={{ color: "rgba(26,26,25,0.6)" }}>
            We typically respond within 24–48 business hours, Monday–Friday.
          </p>
        </div>
      </section>

      {/* ==================== ZOOM LIGHTBOX ==================== */}
      {zoomOpen && product && gallery[mainIdx] && (
        <div
          className="fixed inset-0 z-[100] flex flex-col"
          style={{ background: "rgba(14,14,13,0.96)" }}
          role="dialog"
          aria-modal="true"
          aria-label="Product image zoom view"
          data-testid="sm-zoom-lightbox"
          onClick={() => setZoomOpen(false)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setZoomOpen(false);
            }}
            className="absolute top-5 right-5 z-10 p-2 hover:opacity-70 transition-opacity"
            style={{ color: "#F7F5F2" }}
            aria-label="Close zoom view"
            data-testid="sm-zoom-close"
          >
            <X size={22} strokeWidth={1.5} />
          </button>

          {/* Scrollable image area — supports native pinch-zoom on touch devices
              and mouse-wheel scroll on desktop for panning within a large image.
              Clicking on the empty area around the image closes the lightbox;
              clicks on the image itself do not. */}
          <div
            className="flex-1 overflow-auto"
            style={{ touchAction: "pinch-zoom", WebkitOverflowScrolling: "touch" }}
            data-testid="sm-zoom-scroll"
          >
            <div className="min-h-full flex items-center justify-center p-4 sm:p-10">
              <img
                src={gallery[mainIdx].src}
                alt={product.name}
                className="max-w-none select-none"
                style={{
                  maxHeight: "none",
                  width: "auto",
                  height: "auto",
                  display: "block",
                  cursor: "default",
                }}
                draggable={false}
                onClick={(e) => e.stopPropagation()}
                data-testid="sm-zoom-image"
              />
            </div>
          </div>

          {/* Thumbnail strip inside lightbox — lets user swap without exiting. */}
          <div
            className="flex-shrink-0 flex gap-2 sm:gap-3 items-center justify-center px-4 py-4 overflow-x-auto"
            style={{ background: "rgba(14,14,13,0.6)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="sm-zoom-thumbstrip"
          >
            {gallery.map((img, i) => (
              <button
                key={i}
                onClick={() => setMainIdx(i)}
                className="flex-shrink-0"
                style={{
                  width: 56,
                  height: 56,
                  background: "#EFEBE4",
                  outline: mainIdx === i ? "1px solid #F7F5F2" : "1px solid transparent",
                  outlineOffset: "2px",
                  transition: "outline-color 200ms ease",
                }}
                aria-label={`Zoom to image ${i + 1}`}
                data-testid={`sm-zoom-thumb-${i}`}
              >
                <img
                  src={img.src}
                  alt=""
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 30%" }}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
